import React, { useCallback } from "react";
import PropTypes from "prop-types";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import "@inovua/reactdatagrid-community/index.css";
import { EmptyRow } from "../../components";
import useCellContextCopy from "../../hooks/useCellContextCopy";

const gridStyle = { minHeight: 500, borderRadius: 4, overflow: "hidden" };

const i18n = Object.assign({}, ReactDataGrid.defaultProps.i18n, {
  pageText: "Страница ",
  ofText: " из ",
  perPageText: "Элементов на странице",
  showingText: "Показано ",
  clearAll: "Очистить все",
  clear: "Очистить",
  showFilteringRow: "Показать фильтры",
  hideFilteringRow: "Скрыть фильтры",
  enable: "Включить",
  disable: "Выключить",
  sortAsc: "По возрастанию",
  sortDesc: "По убыванию",
  unsort: "Без сортировки",
  group: "Group",
  ungroup: "Ungroup",
  lockStart: "Lock start",
  lockEnd: "Lock end",
  unlock: "Unlock",
  columns: "Колонки",
  contains: "Содержит",
  startsWith: "Начинается с",
  endsWith: "Заканчивается на",
  notContains: "Не содержит",
  inlist: "В списке",
  notinlist: "Не в списке",
  neq: "Не эквивалентно",
  inrange: "В диапазоне",
  notinrange: "Вне диапазона",
  eq: "Эквивалентно",
  notEmpty: "Не пусто",
  empty: "Пусто",
  lt: "Меньше чем",
  lte: "Меньше чем или равно",
  gt: "Больше чем",
  gte: "Больше чем или равно",
  before: "До",
  beforeOrOn: "До или на",
  afterOrOn: "После или на",
  after: "После",
  start: "Начало",
  end: "Конец",
  dragHeaderToGroup: "Переместите заголовок для группировки",
  "calendar.todayButtonText": "Сегодня",
  "calendar.clearButtonText": "Очистить",
  "calendar.okButtonText": "OK",
  "calendar.cancelButtonText": "Отмена",
});

const pleaseWait = <b>Идет обновление записей...</b>;

const SelectableTable = (props) => {
  const { items, fields, filter, selected, filtering, onSelectionChange, onEditComplete, idProperty, loading, onRowClick, sortFunctions, onReady, setGridRef } = props;

  const { handleCellContextMenu } = useCellContextCopy();

  const scrollProps = Object.assign({}, ReactDataGrid.defaultProps.scrollProps, {
    autoHide: false,
    alwaysShowTrack: true,
  });

  const handleCellClick = useCallback((event, rowData) => {
    console.log("Cell log", event);
    console.log("Cell log", rowData);
    if (rowData.id === 'action') {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (onRowClick && typeof onRowClick === 'function') {
      onRowClick(event, rowData);
    }
  }, [onRowClick]);

  const processedColumns = fields.map(field => {
    const originalRender = field.render;
    const fieldName = field.name;
    const header = field.header;

    return {
      headerAlign: "center",
      ...field,
      render: (renderProps) => {
        const { value, cellProps } = renderProps;

        // Для колонки comment не вешаем копирование, оставляем стандартное поведение для меню
        if (fieldName === "comment" || header === "Комментарий") {
          return originalRender ? originalRender(renderProps) : (value || "—");
        }

        // Для всех остальных колонок добавляем копирование по правому клику
        return (
          <span 
            onContextMenu={(e) => handleCellContextMenu(e, { header: cellProps?.header || field.header, value })} 
            style={{ cursor: 'pointer', display: 'block', width: '100%' }}
          >
            {originalRender ? originalRender(renderProps) : (value !== undefined ? value : "—")}
          </span>
        );
      }
    };
  });

  // Встроенный обработчик контекстного меню строки (срабатывает для комментария)
  const defaultRenderRowContextMenu = (menuProps, { rowProps, cellProps }) => {
    const { data } = rowProps;
    const columnName = cellProps?.name;
    const header = cellProps?.header;

    if (columnName !== "comment" || header === "Комментарий") {
      menuProps.items = [];
      return;
    }

    const comment = data?.comment?.trim() || data?.[columnName];

    if (!comment) {
      menuProps.items = []; 
      return;
    }

    menuProps.autoDismiss = true;
    menuProps.items = [
      {
        label: `${comment}`,
        disabled: true, 
        style: {
          maxWidth: 300,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: "1.4",
          color: "#666",
        },
      },
    ];
  };

  return (
    <ReactDataGrid
      handle={setGridRef}
      idProperty={idProperty}
      style={gridStyle}
      i18n={i18n}
      multiSelect
      selected={selected}
      checkboxColumn
      checkboxOnlyRowSelect
      onSelectionChange={onSelectionChange}
      columns={processedColumns}
      dataSource={items}
      loading={loading}
      loadingText={pleaseWait}
      onEditComplete={onEditComplete}
      emptyText={<EmptyRow />}
      pagination
      defaultLimit={100}
      defaultFilterValue={filter}
      showColumnMenuLockOptions={false}
      showColumnMenuGroupOptions={false}
      showColumnMenuFilterOptions={false}
      enableFiltering={filtering ? filtering : false}
      showFilteringRow={false}
      showColumnMenuToolOnHover={false}
      onCellClick={handleCellClick}
      renderRowContextMenu={defaultRenderRowContextMenu}
      scrollProps={scrollProps}
      enableKeyboardNavigation={false}
      editable={props.editable ?? false}
      sortFunctions={sortFunctions}
      onReady={onReady}
    />
  );
};

SelectableTable.propTypes = {
  items: PropTypes.array.isRequired,
  fields: PropTypes.array.isRequired,
};

export default React.memo(SelectableTable);